import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "../firebase/db";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { Card } from "../types";
import BinderView from "../components/BinderView";
import CardGrid from "../components/CardGrid";
import CardDetailModal from "../components/CardDetailModal";

const TABS = ["Binder", "Inventory", "Wishlist"] as const;
type Tab = typeof TABS[number];

const PublicProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [username, setUsername] = useState<string | null>(null);
  const [binders, setBinders] = useState<any[]>([]);
  const [inventory, setInventory] = useState<Card[]>([]);
  const [wishlist, setWishlist] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBinder, setSelectedBinder] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("Binder");
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);

  // For Inventory tab
  const [inventoryFilter, setInventoryFilter] = useState({
    name: "",
    binderStatus: "all", // "all" | "inBinder" | "notInBinder"
  });

  // For Wishlist tab
  const [wishlistFilter, setWishlistFilter] = useState({
    name: "",
    binderStatus: "all", // "all" | "inBinder" | "notInBinder"
  });

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    getDoc(doc(db, "users", userId)).then(snap => {
      setUsername(
        snap.data()?.username ||
        snap.data()?.displayName ||
        snap.data()?.email ||
        "User"
      );
    });
    Promise.all([
      getDocs(collection(db, "users", userId, "binders")),
      getDocs(collection(db, "users", userId, "inventory")),
      getDocs(collection(db, "users", userId, "wishlist")),
    ]).then(([binderSnap, invSnap, wishSnap]) => {
      setBinders(binderSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setInventory(invSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Card)));
      setWishlist(wishSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Card)));
      setLoading(false);
    });
  }, [userId]);

  // Helper to get binder name by id
  const getBinderName = (binderId: string | undefined) => {
    if (!binderId) return null;
    const binder = binders.find(b => b.id === binderId);
    return binder ? binder.name : null;
  };

  // Inventory filter
  const filteredInventory = inventory.filter(card => {
    const matchesName = card.name.toLowerCase().includes(inventoryFilter.name.toLowerCase());
    const inBinder = !!card.assignedBinderId;
    if (inventoryFilter.binderStatus === "inBinder" && !inBinder) return false;
    if (inventoryFilter.binderStatus === "notInBinder" && inBinder) return false;
    return matchesName;
  });

  // Wishlist filter
  const filteredWishlist = wishlist.filter(card => {
    const matchesName = card.name.toLowerCase().includes(wishlistFilter.name.toLowerCase());
    const inBinder = !!card.assignedBinderId;
    if (wishlistFilter.binderStatus === "inBinder" && !inBinder) return false;
    if (wishlistFilter.binderStatus === "notInBinder" && inBinder) return false;
    return matchesName;
  });

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="bg-blue-100 min-h-screen w-full">
      <div className="max-w-3xl mx-auto w-full px-2 py-4">
        <h1 className="text-2xl font-bold mb-4">{username}&apos;s Public Profile</h1>
        {/* Tabs */}
        <div className="flex border-b mb-6 bg-white rounded-t-xl shadow-sm overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab}
              className={`flex-1 py-3 text-center font-semibold transition-all duration-200 outline-none
                ${activeTab === tab
                  ? "border-b-4 border-indigo-500 text-indigo-700 bg-indigo-50"
                  : "text-gray-500 hover:text-indigo-600 hover:bg-gray-50"
                }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="mt-4">
          {activeTab === "Binder" && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              {binders.map((binder: any, idx: number) => (
                <div
                  key={binder.id}
                  className="bg-white rounded-xl shadow-md flex flex-col items-center justify-center text-center cursor-pointer w-36 h-36 relative border border-gray-100 hover:shadow-lg transition-all duration-200"
                  onClick={() => setSelectedBinder(binder)}
                  title="View Binder"
                >
                  <div className="font-bold text-indigo-700">{binder.name || `Binder ${idx + 1}`}</div>
                  <div className="text-xs text-gray-500">{binder.pages?.length || binder.pages} pages</div>
                  <div className="text-xs text-gray-500">
                    Slots: {binder.columns} × {binder.rows}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "Inventory" && (
            <>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  className="border rounded px-2 py-1 flex-1"
                  placeholder="Filter by name..."
                  value={inventoryFilter.name}
                  onChange={e => setInventoryFilter(f => ({ ...f, name: e.target.value }))}
                />
                <select
                  className="border rounded px-2 py-1"
                  value={inventoryFilter.binderStatus}
                  onChange={e => setInventoryFilter(f => ({ ...f, binderStatus: e.target.value }))}
                >
                  <option value="all">All Cards</option>
                  <option value="inBinder">In Binder</option>
                  <option value="notInBinder">Not in Binder</option>
                </select>
              </div>
              <CardGrid
                cards={filteredInventory}
                getBinderName={getBinderName}
                onCardClick={setSelectedCard}
                readOnly={true}
              />
            </>
          )}

          {activeTab === "Wishlist" && (
            <>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  className="border rounded px-2 py-1 flex-1"
                  placeholder="Filter by name..."
                  value={wishlistFilter.name}
                  onChange={e => setWishlistFilter(f => ({ ...f, name: e.target.value }))}
                />
                <select
                  className="border rounded px-2 py-1"
                  value={wishlistFilter.binderStatus}
                  onChange={e => setWishlistFilter(f => ({ ...f, binderStatus: e.target.value }))}
                >
                  <option value="all">All Cards</option>
                  <option value="inBinder">In Binder</option>
                  <option value="notInBinder">Not in Binder</option>
                </select>
              </div>
              <CardGrid
                cards={filteredWishlist}
                getBinderName={getBinderName}
                onCardClick={setSelectedCard}
                readOnly={true}
              />
            </>
          )}
        </div>
      </div>

      {/* Read-only Binder Modal */}
      {selectedBinder && (
        <BinderView
          binder={selectedBinder}
          onClose={() => setSelectedBinder(null)}
          inventory={inventory}
          wishlist={wishlist}
          onSlotClick={undefined} // disables slot interaction
        />
      )}

      {/* Card Detail Modal */}
      {selectedCard && (
        <CardDetailModal card={selectedCard} onClose={() => setSelectedCard(null)} />
      )}
    </div>
  );
};

export default PublicProfile;